import { useEffect, useState } from 'react'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { ShieldAlert } from 'lucide-react'
import { TopNav } from '../components/TopNav'
import { NAV_ITEMS, Sidebar } from '../components/Sidebar'
import { useMe } from '../lib/auth'
import { landingFor, pathAllowed, portalRole, ROLE_LABEL } from '../lib/rbac'
import { LoginPage } from './LoginPage'

const SIDE_KEY = 'portal.sidebar'

export function RootLayout() {
  const { data: me, isLoading } = useMe()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem(SIDE_KEY) === 'collapsed')

  // RBAC: a persona that lands on (or directly types) a forbidden URL gets
  // bounced to its own landing page. Hooks must run unconditionally, so this
  // sits above the early returns; it's a no-op until `me` is known.
  const role = me ? portalRole(me) : null
  const denied = !!role && !pathAllowed(role, pathname)
  useEffect(() => {
    if (role && denied) navigate({ to: landingFor(role) })
  }, [role, denied, navigate])

  // Gate the whole back-office shell behind a UMS session. The standalone
  // /inbox WebView hangs off the root route (not this layout), so it stays
  // public for the mobile app.
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <p className="muted">กำลังตรวจสอบสิทธิ์…</p>
      </div>
    )
  }

  if (!me) return <LoginPage />

  return (
    <div className={`bo-shell${collapsed ? ' side-collapsed' : ''}`}>
      <Sidebar />
      <div className="bo-right">
        <TopNav
          onToggleSidebar={() => {
            setCollapsed((v) => {
              sessionStorage.setItem(SIDE_KEY, v ? 'open' : 'collapsed')
              return !v
            })
          }}
        />
        <Breadcrumb />
        <main className="bo-main">
          {denied ? (
            <div className="panel access-denied" role="alert">
              <ShieldAlert size={22} />
              <div>
                <h2>ไม่มีสิทธิ์เข้าถึงเมนูนี้</h2>
                <p className="muted">
                  บทบาท <b>{role ? ROLE_LABEL[role] : ''}</b> ไม่ได้รับสิทธิ์ในหน้านี้
                  — กำลังพากลับไปยังหน้าหลักของบทบาท…
                </p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  )
}

// CoreUI-style breadcrumb under the navbar: หน้าหลัก / <เมนู> / <รายการ>
function Breadcrumb() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const segments = pathname.split('/').filter(Boolean)
  const nav = NAV_ITEMS.find((n) => n.to === `/${segments[0] ?? ''}`)
  const detail = segments.slice(1).join('/')

  return (
    <div className="breadcrumb-bar" aria-label="breadcrumb">
      <Link to="/dashboard">หน้าหลัก</Link>
      {nav && (
        <>
          <span className="sep">/</span>
          {detail ? <Link to={nav.to}>{nav.label}</Link> : <span className="current">{nav.label}</span>}
        </>
      )}
      {nav && detail && (
        <>
          <span className="sep">/</span>
          <span className="current"><code>{decodeURIComponent(detail)}</code></span>
        </>
      )}
    </div>
  )
}
